import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  Brain, Sparkles, Send, Paperclip, Globe,
  Layers, BarChart3, FileText, Clock, Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PariChat = () => {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const handleSend = async () => {
    if (!message.trim() || isLoading) return;
    const userMessage = { type: "user", content: message };
    setMessages(prev => [...prev, userMessage]);
    setMessage("");
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("message", message);
      if (sessionId) formData.append("session_id", sessionId);
      const response = await axios.post(`${BACKEND_URL}/api/chat`, formData);
      setMessages(prev => [...prev, {
        type: "assistant",
        content: response.data.response,
        sources: response.data.sources || [],
      }]);
      if (response.data.session_id) setSessionId(response.data.session_id);
    } catch {
      setMessages(prev => [...prev, {
        type: "assistant",
        content: "Sorry, I encountered an error. Please make sure the backend server is running and try again.",
        sources: [],
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const examplePrompts = [
    {
      icon: <Layers className="h-5 w-5" />,
      title: "Agricultural Coverage",
      text: "Design drought insurance for rice farmers in Karnataka",
      color: "text-emerald-600",
      bg: "bg-emerald-50 border-emerald-100",
    },
    {
      icon: <FileText className="h-5 w-5" />,
      title: "Natural Catastrophe",
      text: "Create flood risk model for coastal areas",
      color: "text-blue-600",
      bg: "bg-blue-50 border-blue-100",
    },
    {
      icon: <BarChart3 className="h-5 w-5" />,
      title: "Data Analysis",
      text: "Analyze historical claims patterns and trends",
      color: "text-violet-600",
      bg: "bg-violet-50 border-violet-100",
    },
    {
      icon: <Clock className="h-5 w-5" />,
      title: "Pricing Model",
      text: "Generate premium calculation framework",
      color: "text-amber-600",
      bg: "bg-amber-50 border-amber-100",
    },
  ];

  return (
    <div className="bg-background flex flex-col" style={{ height: "calc(100vh - 56px)" }}>

      {/* ── Vibrant Hero Header ── */}
      <div className="relative overflow-hidden shrink-0"
        style={{ background: "linear-gradient(135deg, #0a1628 0%, #0f2847 55%, #0054a8 100%)" }}>
        {/* Decorative glow blobs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-10 left-1/4 w-80 h-80 rounded-full blur-3xl"
            style={{ background: "radial-gradient(circle, rgba(0,118,215,0.18) 0%, transparent 70%)" }} />
          <div className="absolute -bottom-10 right-1/5 w-80 h-80 rounded-full blur-3xl"
            style={{ background: "radial-gradient(circle, rgba(0,200,255,0.12) 0%, transparent 70%)" }} />
          <div className="absolute top-1/2 left-1/2 w-48 h-48 rounded-full blur-2xl -translate-x-1/2 -translate-y-1/2"
            style={{ background: "radial-gradient(circle, rgba(99,102,241,0.14) 0%, transparent 70%)" }} />
        </div>

        <div className="max-w-3xl mx-auto px-6 py-8 relative z-10 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-4 border"
            style={{ background: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.15)" }}>
            <Sparkles className="h-3.5 w-3.5 text-cyan-300" />
            <span className="text-xs font-bold tracking-widest uppercase text-cyan-100">pycrop Intelligence</span>
          </div>

          <h1 className="font-heading font-bold text-white mb-2 tracking-tight"
            style={{ fontSize: "clamp(1.5rem, 4vw, 2.25rem)" }}>
            Parametric Product Designer
          </h1>
          <p className="text-sm max-w-lg mx-auto leading-relaxed" style={{ color: "rgba(147,197,253,0.85)" }}>
            Describe your risk parameters or upload historical data to design a new parametric insurance product with AI guidance.
          </p>

          {/* Status pill */}
          <div className="inline-flex items-center gap-2 mt-4 px-3 py-1 rounded-full"
            style={{ background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.25)" }}>
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs font-semibold text-emerald-300">Online</span>
          </div>
        </div>
      </div>

      {/* ── Chat / Empty State ── */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-3xl mx-auto">
          {messages.length === 0 ? (
            <div className="mt-2">
              <p className="text-center text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-5">
                Try one of these to get started
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {examplePrompts.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => setMessage(p.text)}
                    className={`text-left p-5 rounded-xl border-2 bg-card hover:shadow-md hover:-translate-y-1 transition-all duration-200 group ${p.bg}`}
                  >
                    <div className={`w-9 h-9 rounded-lg border flex items-center justify-center mb-3 ${p.bg} ${p.color} group-hover:scale-110 transition-transform`}>
                      {p.icon}
                    </div>
                    <div className="font-semibold text-sm text-foreground mb-1">{p.title}</div>
                    <div className="text-xs text-muted-foreground leading-relaxed">{p.text}</div>
                    <div className={`text-xs font-semibold mt-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ${p.color}`}>
                      Use this prompt
                      <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-5 mb-4">
              {messages.map((msg, i) => (
                <div key={i} className={`flex gap-3 ${msg.type === "user" ? "justify-end" : "justify-start"}`}>
                  {msg.type === "assistant" && (
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm"
                      style={{ background: "linear-gradient(135deg, #0076d7, #00a8ff)" }}>
                      <Brain className="h-4 w-4 text-white" />
                    </div>
                  )}
                  <div className={`${msg.type === "user" ? "max-w-[78%]" : "flex-1 max-w-[85%]"}`}>
                    <div className={`px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap shadow-sm ${
                      msg.type === "user"
                        ? "rounded-2xl rounded-br-sm text-white"
                        : "rounded-2xl rounded-bl-sm bg-card border border-border text-foreground"
                    }`}
                      style={msg.type === "user" ? { background: "linear-gradient(135deg, #0076d7, #0054a8)" } : {}}>
                      {msg.content}
                    </div>
                    {msg.sources?.length > 0 && (
                      <div className="mt-2 text-xs text-muted-foreground space-y-1 pl-1">
                        <div className="font-semibold">Sources:</div>
                        {msg.sources.map((s, si) => (
                          <a key={si} href={s.url} target="_blank" rel="noopener noreferrer"
                            className="block text-blue-500 hover:underline truncate">
                            [{si + 1}] {s.title}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  {msg.type === "user" && (
                    <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-muted-foreground">U</span>
                    </div>
                  )}
                </div>
              ))}

              {isLoading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm"
                    style={{ background: "linear-gradient(135deg, #0076d7, #00a8ff)" }}>
                    <Brain className="h-4 w-4 text-white" />
                  </div>
                  <div className="px-4 py-3 rounded-2xl rounded-bl-sm bg-card border border-border text-sm text-muted-foreground flex items-center gap-2 shadow-sm">
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />
                    <span>Thinking…</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>

      {/* ── Input Bar ── */}
      <div className="shrink-0 border-t border-border bg-background/90 backdrop-blur-sm px-4 py-4">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-xl border-2 border-border bg-card shadow-sm focus-within:border-blue-500 focus-within:shadow-[0_0_0_3px_rgba(0,118,215,0.08)] transition-all duration-200">
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Describe your insurance product requirements…"
              rows={3}
              className="w-full px-4 pt-4 pb-2 text-sm text-foreground bg-transparent resize-none outline-none placeholder:text-muted-foreground leading-relaxed"
            />
            <div className="flex items-center justify-between px-3 pb-3 pt-1 border-t border-border">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted hover:text-foreground transition-colors"
                >
                  <Paperclip className="h-3.5 w-3.5" /> Attach
                </button>
                <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted hover:text-foreground transition-colors">
                  <Globe className="h-3.5 w-3.5" /> Web
                </button>
                <input type="file" ref={fileInputRef} className="hidden" multiple />
              </div>

              <Button
                onClick={handleSend}
                disabled={!message.trim() || isLoading}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2 rounded-lg px-4 shadow-sm"
              >
                {isLoading
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <><span>Send</span><Send className="h-3.5 w-3.5" /></>}
              </Button>
            </div>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-3">
            pycrop AI · Parametric Insurance Intelligence · Press <kbd className="px-1 py-0.5 rounded border border-border bg-muted text-[10px]">Enter</kbd> to send
          </p>
        </div>
      </div>
    </div>
  );
};

export default PariChat;